import { type FC, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { Link } from "react-router";
import { toaster } from "@/components/ui/toaster";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import Logo from "../assets/logo.svg";
import img1 from "../assets/img1.jpg";
import img2 from "../assets/img2.jpg";
import img3 from "../assets/img3.jpg";
import img4 from "../assets/img4.jpg";

const ForgotPasswordPage: FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    setLoading(true);

    const promise = new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 1000);
    });

    toaster.promise(promise, {
      loading: {
        title: "Sending reset link...",
        description: "Please wait",
      },
      success: {
        title: "Reset link sent",
        description: "If this email exists, a reset link has been sent.",
      },
      error: {
        title: "Request failed",
        description: "Please try again.",
      },
    });

    promise.finally(() => setLoading(false));
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "common.white" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: { xs: "100%", md: "50%" },
          alignItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 480 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 6,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ p: 1, borderRadius: 2 }}>
              <img src={Logo} height={40} width={40} alt="SheetX Logo" />
            </Box>
            <Typography sx={{ color: "text.primary", fontWeight: 700, fontSize: "42px" }}>
              SheetX
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "common.white", borderRadius: 4, p: 4 }}>
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "1.875rem", fontWeight: 700, color: "text.primary", mb: 1 }}
              >
                Forgot Password
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                Enter your email and we will send you a password reset link
              </Typography>
            </Box>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (document.activeElement && document.activeElement.tagName === "TEXTAREA") {
                  return;
                }
                handleSubmit();
              }}
            >
              <Stack spacing={3}>
                <TextField
                  required
                  label="Email"
                  type="email"
                  placeholder="Enter your email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  fullWidth
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Link
                    to="/"
                    style={{
                      color: "#1976d2",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Back to sign in
                  </Link>
                </Box>
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  disabled={loading || !email.trim()}
                  sx={{
                    background: "#1976d2",
                    color: "#fff",
                    borderRadius: 3,
                    fontWeight: 600,
                    textTransform: "none",
                    transition: "all 0.2s",
                    "&:hover": {
                      background: "#1565c0",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  Send Reset Link
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: "50%", display: { xs: "none", md: "block" }, height: "100vh" }}>
        <Carousel
          autoPlay
          infiniteLoop
          showThumbs={false}
          showArrows={false}
          showStatus={false}
          interval={5000}
          transitionTime={1000}
        >
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img1}
              alt="Cold storage facility"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img2}
              alt="Warehouse management"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img3}
              alt="Inventory tracking"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box sx={{ height: "100vh", overflow: "hidden" }}>
            <img
              src={img4}
              alt="Supply chain network"
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </Carousel>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
